import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import clsx from "clsx";
import {
  Box,
  CircularProgress,
  Divider,
  Fab,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Zoom,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import SendIcon from "@material-ui/icons/Send";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import AndroidIcon from "@material-ui/icons/Android";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  loadSelectedPromptId,
  resolveSelectedPromptId,
  saveSelectedPromptId,
} from "../../utils/chatAgentPromptStorage";

const useStyles = makeStyles((theme) => ({
  fab: {
    position: "fixed",
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 1400,
    boxShadow: theme.shadows[8],
    transition: theme.transitions.create("bottom", {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeInOut,
    }),
  },
  fabInTicketChat: {
    bottom: theme.spacing(10),
  },
  panel: {
    position: "fixed",
    bottom: theme.spacing(11),
    right: theme.spacing(3),
    width: 380,
    maxWidth: "calc(100vw - 24px)",
    height: 520,
    maxHeight: "calc(100vh - 120px)",
    zIndex: 1400,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 16,
    boxShadow: theme.shadows[12],
    transition: theme.transitions.create("bottom", {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeInOut,
    }),
  },
  panelInTicketChat: {
    bottom: theme.spacing(18),
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2),
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 600,
    minWidth: 0,
    flex: 1,
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
  },
  settingsMenuPaper: {
    minWidth: 260,
    maxWidth: 320,
  },
  settingsMenuTitle: {
    padding: theme.spacing(1.5, 2, 0.5),
    fontSize: "0.75rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  messages: {
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(1.5),
    backgroundColor:
      theme.palette.type === "dark" ? "#1e293b" : "#eef2f7",
    ...theme.scrollbarStyles,
  },
  bubbleUser: {
    marginLeft: "auto",
    marginBottom: theme.spacing(1),
    maxWidth: "85%",
    padding: theme.spacing(1, 1.5),
    borderRadius: 12,
    borderBottomRightRadius: 4,
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: 14,
  },
  bubbleAssistant: {
    marginRight: "auto",
    marginBottom: theme.spacing(1),
    maxWidth: "90%",
    padding: theme.spacing(1, 1.5),
    borderRadius: 12,
    borderBottomLeftRadius: 4,
    backgroundColor:
      theme.palette.type === "dark" ? "#334155" : "#ffffff",
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontSize: 14,
    position: "relative",
  },
  copyBtn: {
    position: "absolute",
    top: 2,
    right: 2,
    padding: 4,
  },
  inputArea: {
    padding: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  emptyState: {
    padding: theme.spacing(3, 2),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
}));

const PersonalAssistantWidget = () => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const messagesEndRef = useRef(null);

  const isTicketChatView = /^\/tickets\/[^/?#]+/.test(location.pathname);

  const [open, setOpen] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState(() => {
    if (!user?.companyId) return "";
    const stored = loadSelectedPromptId(user.companyId);
    return stored ? String(stored) : "";
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [settingsAnchor, setSettingsAnchor] = useState(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (open) {
      scrollToBottom();
    }
  }, [messages, open]);

  useEffect(() => {
    if (!user?.companyId) return;
    const stored = loadSelectedPromptId(user.companyId);
    if (stored) {
      setSelectedPromptId(String(stored));
    }
  }, [user?.companyId]);

  useEffect(() => {
    if (!user?.companyId) return;

    const loadPrompts = async () => {
      setLoadingPrompts(true);
      try {
        let list = [];
        try {
          const { data } = await api.get("/assistant/prompts");
          list = data || [];
        } catch (assistantErr) {
          const { data } = await api.get("/prompt");
          list = (data?.prompts || []).map((prompt) => ({
            id: prompt.id,
            name: prompt.name,
            model: prompt.model,
          }));
          if (!list.length) {
            throw assistantErr;
          }
        }

        setPrompts(list);

        setSelectedPromptId((currentId) => {
          const resolved = resolveSelectedPromptId({
            companyId: user.companyId,
            prompts: list,
            currentId: currentId ? Number(currentId) : null,
          });
          if (resolved) return String(resolved);
          return list.length ? String(list[0].id) : "";
        });
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingPrompts(false);
      }
    };

    loadPrompts();
  }, [user?.companyId]);

  const handlePromptChange = (promptId) => {
    const value = String(promptId);
    setSelectedPromptId(value);
    saveSelectedPromptId(user?.companyId, Number(promptId));
    setSettingsAnchor(null);
  };

  const handleOpenSettings = (event) => {
    setSettingsAnchor(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setSettingsAnchor(null);
  };

  const selectedPrompt = prompts.find(
    (prompt) => String(prompt.id) === selectedPromptId
  );

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(i18n.t("assistant.copied"));
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    if (!selectedPromptId && !prompts.length) {
      toast.error(i18n.t("assistant.noPrompt"));
      return;
    }

    const userMessage = { role: "user", content: text };
    const history = messages.map((m) => ({
      role: m.role,
      content: m.content,
    }));

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const { data } = await api.post(
        "/assistant/message",
        {
          message: text,
          history,
          promptId: selectedPromptId ? Number(selectedPromptId) : undefined,
        },
        { timeout: 60000 }
      );

      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: data.reply },
      ]);
    } catch (err) {
      setMessages((prev) => prev.slice(0, -1));
      setInput(text);
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  if (!user?.id) return null;

  return (
    <>
      <Zoom in>
        <Tooltip title={i18n.t("assistant.open")} placement="left">
          <Fab
            color="primary"
            className={clsx(classes.fab, isTicketChatView && classes.fabInTicketChat)}
            onClick={() => setOpen((prev) => !prev)}
            aria-label={i18n.t("assistant.open")}
          >
            {open ? <CloseIcon /> : <AndroidIcon />}
          </Fab>
        </Tooltip>
      </Zoom>

      {open && (
        <Paper
          className={clsx(classes.panel, isTicketChatView && classes.panelInTicketChat)}
          elevation={8}
        >
          <Box className={classes.header}>
            <Typography className={classes.headerTitle} variant="subtitle1" noWrap>
              <AndroidIcon fontSize="small" />
              {i18n.t("assistant.title")}
            </Typography>
            <Box className={classes.headerActions}>
              <Tooltip title={i18n.t("assistant.settings")}>
                <IconButton
                  size="small"
                  onClick={handleOpenSettings}
                  style={{ color: "inherit" }}
                  aria-label={i18n.t("assistant.settings")}
                >
                  <SettingsOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                onClick={() => setOpen(false)}
                style={{ color: "inherit" }}
                aria-label={i18n.t("common.close", { defaultValue: "Cerrar" })}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Menu
            anchorEl={settingsAnchor}
            open={Boolean(settingsAnchor)}
            onClose={handleCloseSettings}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            getContentAnchorEl={null}
            classes={{ paper: classes.settingsMenuPaper }}
          >
            <Typography className={classes.settingsMenuTitle}>
              {i18n.t("assistant.model")}
            </Typography>
            {loadingPrompts ? (
              <Box display="flex" justifyContent="center" py={2} px={2}>
                <CircularProgress size={22} />
              </Box>
            ) : prompts.length > 0 ? (
              prompts.map((prompt) => (
                <MenuItem
                  key={prompt.id}
                  selected={String(prompt.id) === selectedPromptId}
                  onClick={() => handlePromptChange(prompt.id)}
                >
                  {prompt.name} ({prompt.model})
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>{i18n.t("assistant.noPrompt")}</MenuItem>
            )}
            {selectedPrompt && (
              <Box px={2} py={1}>
                <Typography variant="caption" color="textSecondary">
                  {i18n.t("assistant.activeIntegration", {
                    defaultValue: "Activa: {{name}}",
                    name: `${selectedPrompt.name} (${selectedPrompt.model})`,
                  })}
                </Typography>
              </Box>
            )}
            <Divider />
            <MenuItem
              onClick={() => {
                handleCloseSettings();
                history.push("/connections");
              }}
            >
              {i18n.t("assistant.goSetup")}
            </MenuItem>
          </Menu>

          <Box className={classes.messages}>
            {!loadingPrompts && !prompts.length && (
              <Box className={classes.emptyState} style={{ marginBottom: 8 }}>
                <Typography variant="body2" color="error" gutterBottom>
                  {i18n.t("assistant.noPrompt")}
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  style={{ cursor: "pointer", textDecoration: "underline" }}
                  onClick={() => history.push("/connections")}
                >
                  {i18n.t("assistant.goSetup")}
                </Typography>
              </Box>
            )}
            {messages.length === 0 && (
              <Box className={classes.emptyState}>
                <AndroidIcon
                  style={{ fontSize: 40, opacity: 0.4, marginBottom: 8 }}
                />
                <Typography variant="body2">
                  {i18n.t("assistant.welcome")}
                </Typography>
              </Box>
            )}

            {messages.map((msg, index) =>
              msg.role === "user" ? (
                <Box key={index} className={classes.bubbleUser}>
                  {msg.content}
                </Box>
              ) : (
                <Box key={index} className={classes.bubbleAssistant}>
                  <Tooltip title={i18n.t("assistant.copy")}>
                    <IconButton
                      className={classes.copyBtn}
                      size="small"
                      onClick={() => handleCopy(msg.content)}
                    >
                      <FileCopyOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Box pr={3}>{msg.content}</Box>
                </Box>
              )
            )}

            {loading && (
              <Box display="flex" alignItems="center" p={1} style={{ gap: 8 }}>
                <CircularProgress size={18} />
                <Typography variant="caption" color="textSecondary">
                  {i18n.t("assistant.thinking")}
                </Typography>
              </Box>
            )}
            <div ref={messagesEndRef} />
          </Box>

          <Box className={classes.inputArea}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              variant="outlined"
              size="small"
              placeholder={i18n.t("assistant.placeholder")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={loading || (!prompts.length && !loadingPrompts)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      color="primary"
                      onClick={handleSend}
                      disabled={
                        loading || !input.trim() || !prompts.length
                      }
                    >
                      <SendIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Paper>
      )}
    </>
  );
};

export default PersonalAssistantWidget;
